<template>
    <div class="contents">
        <div class="container">
            <div class="row">
                <div class="col-lg-12">
                    <div class="breadcrumb-main">
                        <h4 class="text-capitalize breadcrumb-title">Beli Paket</h4>
                    </div>

                    <div v-if="isExpired()" class="alert alert-danger" role="alert">
                        <i class="fas fa-exclamation-triangle mr-2"></i>
                        <strong>Akun Anda Telah Expired!</strong> Silakan pilih paket di bawah untuk melanjutkan menggunakan layanan.
                    </div>

                    <div v-if="!isLoading && !myPackageLoading" class="row">
                        <div class="col-lg-12">
                            <div class="card shadow-sm border-0 mb-30">
                                <div class="card-header bg-white border-bottom">
                                    <h5 class="mb-0">
                                        <i class="fas fa-box mr-2 text-primary"></i>
                                        Paket Saya
                                    </h5>
                                </div>
                                <div class="card-body p-30">
                                    <div v-if="myPackage && myPackage.name">
                                        <div class="row">
                                            <div class="col-md-6 mb-4">
                                                <div class="info-box">
                                                    <label class="info-label">Nama Paket</label>
                                                    <div class="info-value">{{ myPackage.name }}</div>
                                                </div>
                                            </div>
                                            <div class="col-md-6 mb-4">
                                                <div class="info-box">
                                                    <label class="info-label">Expired At</label>
                                                    <div class="info-value">
                                                        <span :class="{'text-danger font-weight-bold': isExpired(), 'text-warning font-weight-bold': isExpiringSoon(), 'text-success': !isExpired() && !isExpiringSoon()}">
                                                            <i :class="{'fa-calendar-times': isExpired(), 'fa-calendar-check': !isExpired()}" class="fas mr-1"></i>
                                                            {{ myPackage.expired_at }}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                            <div class="col-md-6 mb-4">
                                                <div class="info-box">
                                                    <label class="info-label">Max Device</label>
                                                    <div class="info-value">{{ myPackage.device }}</div>
                                                </div>
                                            </div>
                                            <div class="col-md-6 mb-4">
                                                <div class="info-box">
                                                    <label class="info-label">Max CS</label>
                                                    <div class="info-value">{{ myPackage.customer_service }}</div>
                                                </div>
                                            </div>
                                            <div class="col-md-6 mb-4">
                                                <div class="info-box">
                                                    <label class="info-label">Max Link Rotator</label>
                                                    <div class="info-value">{{ myPackage.link_rotator }}</div>
                                                </div>
                                            </div>
                                            <div class="col-md-6 mb-4">
                                                <div class="info-box">
                                                    <label class="info-label">Quota Pesan</label>
                                                    <div class="info-value">{{ myPackage.pesan }}</div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div v-else class="text-center p-40">
                                        <i class="fas fa-inbox fa-3x text-muted mb-3"></i>
                                        <p class="text-muted">Anda belum memiliki paket aktif</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div v-else class="text-center p-30">
                        <div class="spinner-border text-primary" role="status">
                            <span class="sr-only">Loading...</span>
                        </div>
                    </div>

                    <!-- Package purchase options -->
                    <div class="row" id="package-list">
                        <div class="col-lg-12">
                            <div class="card shadow-sm border-0 mb-30">
                                <div class="card-header bg-white border-bottom d-flex justify-content-between align-items-center">
                                    <h5 class="mb-0">
                                        <i class="fas fa-shopping-cart mr-2 text-primary"></i>
                                        Pilih Paket
                                    </h5>
                                    <small class="text-muted">Pembayaran: Transfer Bank / PayPal</small>
                                </div>
                                <div class="card-body p-30">
                                    <div v-if="myPackageLoading" class="text-center p-30">
                                        <div class="spinner-border text-primary" role="status"></div>
                                    </div>
                                    <div v-else-if="packageList.length === 0" class="text-center p-40">
                                        <i class="fas fa-inbox fa-3x text-muted mb-3"></i>
                                        <p class="text-muted">Belum ada paket tersedia</p>
                                    </div>
                                    <div v-else class="row">
                                        <div class="col-md-6 col-lg-4 mb-4" v-for="pkg in packageList" :key="pkg.id">
                                            <div class="package-card h-100" :class="{'package-card-active': pkg.isMyPackage}">
                                                <div v-if="pkg.isMyPackage" class="package-badge">Paket Saat Ini</div>
                                                <h5 class="package-name">{{ pkg.name }}</h5>
                                                <div class="package-price">
                                                    <span v-if="pkg.price && pkg.price > 0">{{ formatRupiah(pkg.price) }}</span>
                                                    <span v-else class="text-success">Gratis</span>
                                                </div>
                                                <div class="package-price-usd text-muted" v-if="pkg.price && pkg.price > 0">
                                                    <small>± ${{ (pkg.price / usdRate).toFixed(2) }} via PayPal</small>
                                                </div>
                                                <ul class="package-features list-unstyled">
                                                    <li><i class="fas fa-check text-success mr-2"></i>Quota Pesan: <strong>{{ pkg.pesan }}</strong></li>
                                                    <li><i class="fas fa-check text-success mr-2"></i>Device: <strong>{{ pkg.device }}</strong></li>
                                                    <li><i class="fas fa-check text-success mr-2"></i>Customer Service: <strong>{{ pkg.customer_service }}</strong></li>
                                                    <li><i class="fas fa-check text-success mr-2"></i>Durasi: <strong>{{ pkg.duration }} hari</strong></li>
                                                </ul>
                                                <button
                                                    v-if="pkg.price && pkg.price > 0"
                                                    class="btn btn-primary btn-block"
                                                    :disabled="isPurchasing"
                                                    @click="openPaymentMethod(pkg)">
                                                    {{ pkg.isMyPackage ? 'Perpanjang Paket' : 'Beli Paket' }}
                                                </button>
                                                <div v-else class="text-center text-muted"><small>Paket gratis, hubungi admin</small></div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Payment method modal -->
        <modal name="payment_method" :height="'auto'" :adaptive="true" :scrollable="true">
            <div class="p-30">
                <h5 class="mb-3">Pilih Metode Pembayaran</h5>
                <div class="payment-summary info-box mb-4">
                    <div class="d-flex justify-content-between">
                        <span>Paket</span>
                        <strong>{{ selectedPackage ? selectedPackage.name : '-' }}</strong>
                    </div>
                    <div class="d-flex justify-content-between mt-2">
                        <span>Harga</span>
                        <strong>{{ selectedPackage ? formatRupiah(selectedPackage.price) : '-' }}</strong>
                    </div>
                </div>

                <div class="payment-method-list">
                    <label class="payment-method-option" :class="{'selected': paymentMethod === 'transfer'}">
                        <input type="radio" value="transfer" v-model="paymentMethod">
                        <i class="fas fa-university payment-method-icon"></i>
                        <div class="payment-method-info">
                            <div class="payment-method-title">Transfer Bank</div>
                            <div class="payment-method-desc">Transfer manual ke rekening bank, verifikasi oleh admin (maks 1x24 jam)</div>
                        </div>
                    </label>
                    <label class="payment-method-option" :class="{'selected': paymentMethod === 'paypal'}">
                        <input type="radio" value="paypal" v-model="paymentMethod">
                        <i class="fab fa-paypal payment-method-icon paypal-icon"></i>
                        <div class="payment-method-info">
                            <div class="payment-method-title">PayPal</div>
                            <div class="payment-method-desc">Otomatis dikonversi ke USD, aktivasi instan setelah pembayaran</div>
                        </div>
                    </label>
                </div>

                <div v-if="paymentMethod === 'transfer' && bankAccountList.length > 0" class="mt-3">
                    <label class="info-label mb-2">Pilih Rekening Tujuan Transfer</label>
                    <div v-for="account in bankAccountList" :key="account.id" class="bank-account-option mb-2 p-3 border rounded"
                         :class="{'border-primary bg-light': selectedBankAccount && selectedBankAccount.id === account.id}"
                         @click="selectedBankAccount = account" style="cursor:pointer">
                        <div class="d-flex align-items-center">
                            <input type="radio" :value="account.id" :checked="selectedBankAccount && selectedBankAccount.id === account.id" class="mr-2">
                            <div>
                                <strong>{{ account.bank }}</strong> - {{ account.number }}<br>
                                <small class="text-muted">a.n. {{ account.name }}</small>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="d-flex mt-4">
                    <button class="btn btn-secondary mr-2 flex-fill" @click="closePaymentMethod">Batal</button>
                    <button class="btn btn-primary flex-fill" :disabled="isPurchasing || !paymentMethod || (paymentMethod === 'transfer' && !selectedBankAccount)" @click="confirmPurchase">
                        <span v-if="isPurchasing">Memproses...</span>
                        <span v-else>Lanjutkan Pembayaran</span>
                    </button>
                </div>
            </div>
        </modal>

        <!-- Bank transfer instructions modal -->
        <modal name="bank_instructions" :height="'auto'" :adaptive="true" :scrollable="true">
            <div class="p-30">
                <h5 class="mb-3">Instruksi Transfer Bank</h5>

                <div class="alert alert-warning" role="alert">
                    <i class="fas fa-info-circle mr-2"></i>
                    Transfer <strong>tepat sesuai nominal</strong> dan cantumkan <strong>nomor invoice</strong> pada berita transfer.
                </div>

                <div class="info-box mb-3">
                    <label class="info-label">Nomor Invoice</label>
                    <div class="info-value">{{ bankInstructions.invoice_number }}</div>
                </div>
                <div class="info-box mb-3" v-if="bankInstructions.unique_code">
                    <label class="info-label">Kode Unik</label>
                    <div class="info-value text-warning font-weight-bold" style="font-size:20px">{{ bankInstructions.unique_code }}</div>
                </div>
                <div class="info-box mb-3">
                    <label class="info-label">Total Transfer (termasuk kode unik)</label>
                    <div class="info-value text-primary font-weight-bold" style="font-size:22px">
                        {{ formatRupiah(bankInstructions.total_transfer || bankInstructions.amount_idr) }}
                    </div>
                    <small v-if="bankInstructions.unique_code" class="text-muted d-block mt-1">
                        {{ formatRupiah(bankInstructions.amount_idr) }} + kode unik {{ bankInstructions.unique_code }}
                    </small>
                </div>

                <div v-for="(account, index) in bankInstructions.bank_accounts" :key="index" class="info-box mb-3">
                    <label class="info-label">{{ account.bank }}</label>
                    <div class="info-value">
                        <div class="d-flex justify-content-between align-items-center">
                            <span class="font-weight-bold">{{ account.number }}</span>
                            <button class="btn btn-sm btn-outline-primary" @click="copyText(account.number)">Salin</button>
                        </div>
                        <small class="text-muted">a.n. {{ account.name }}</small>
                    </div>
                </div>

                <p class="text-muted small">
                    Setelah melakukan transfer, pembayaran akan diverifikasi oleh admin maksimal 1x24 jam.
                    Paket aktif otomatis setelah verifikasi.
                </p>

                <button class="btn btn-primary btn-block" @click="$modal.toggle('bank_instructions')">Selesai</button>
            </div>
        </modal>
    </div>
</template>

<script>
    import moment from 'moment'

    export default {
        components: {

        },
        data() {
            return {
                isLoading: true,
                myPackageLoading: true,
                isPurchasing: false,
                paymentMethod: 'transfer',
                selectedPackage: null,
                selectedBankAccount: null,
                bankAccountList: [],
                usdRate: 15000,
                bankInstructions: {
                    invoice_number: '',
                    amount_idr: 0,
                    bank_accounts: []
                },
                userExpiredAt: null,
                myPackage: {
                    id: "",
                    name: "",
                    pesan: "0",
                    device: "0",
                    customer_service: "0",
                    campaign: "0",
                    order_online: "0",
                    link_rotator: "0",
                    chat_widget: "0",
                    expired_at: "",
                    webhook_media_quota:0

                },
                withdrawableBalance: 0,
                user_balance: 0,
                packageList: [],
                extendURL: "",
                syncText: "Sinkronisasi dalam proses..."
            };
        },
        beforeDestroy(){
            this.$root.$off("toggle_modal", this.fnToggleModal);
            this.$root.$off("refresh_data", this.fnRefreshData);
        },
        async mounted() {
            this.fnToggleModal = (param) => {
                this.toggleModal(param);
            }
            this.$root.$on("toggle_modal", this.fnToggleModal);

            this.fnRefreshData = (param) => {
                if(param == "user") this.fetchUser()
            }
            this.$root.$on("refresh_data", this.fnRefreshData);

            this.handlePaypalReturn()

            await this.fetchUser()
            await this.fetchPacakge()
            await this.fetchBankAccounts()
            if(this.$store.state.auth.user.level == 'user-agency'){
                await this.fetchAgencySetting()
            }

            this.isLoading = false
        },
        methods: {
            handlePaypalReturn() {
                const query = this.$route.query
                if (query.payment === 'paypal' && query.status) {
                    if (query.status === 'success') {
                        this.$toast.success('Pembayaran PayPal berhasil! Paket Anda telah diaktifkan.')
                    } else if (query.status === 'cancel') {
                        this.$toast.info('Pembayaran PayPal dibatalkan.')
                    }
                    this.$router.replace({ query: {} }).catch(() => {})
                }
            },
            async fetchUser() {
                try {
                    let user_data = await this.$store.dispatch('user_detail')
                    let user = user_data.data.data.user
                    // Keep auth store in sync so package activation applies immediately
                    this.$store.commit('SET_USER', user)
                    this.$store.commit('SET_ROLE', user.level)
                    this.user_balance = user.balance || 0
                    this.withdrawableBalance = user.withdrawable_balance || 0

                    // Store expired_at for isExpired check
                    this.userExpiredAt = user.expired_at ? moment(user.expired_at) : null

                    // Fetch package information from user
                    this.myPackageLoading = true
                    if (user.package && user.package.id) {
                        // Package data is included in user response
                        this.myPackage.id = user.package.id
                        this.myPackage.name = user.package.name || 'N/A'
                    } else if (user.package_id) {
                        // Package not loaded, fetch it
                        await this.fetchPackageDetails(user.package_id)
                    }

                    // Set user limits and expired date
                    this.myPackage.device = user.max_device == -1 || user.max_device == null ? "Unlimited" : user.max_device
                    this.myPackage.customer_service = user.max_cs == -1 || user.max_cs == null ? "Unlimited" : user.max_cs
                    this.myPackage.link_rotator = user.max_link_rotator == -1 || user.max_link_rotator == null ? "Unlimited" : user.max_link_rotator
                    this.myPackage.pesan = user.max_message == -1 || user.max_message == null ? "Unlimited" : user.max_message

                    // Set expired date from user
                    if (user.expired_at) {
                        this.myPackage.expired_at = moment(user.expired_at).format('DD MMMM YYYY')
                    }

                    this.myPackageLoading = false
                } catch (err) {
                    console.error('Error fetching user:', err)
                    this.$toast.error(err.response?.data?.message || 'Failed to fetch user data')
                    this.myPackageLoading = false
                }
            },
            async fetchPackageDetails(packageId) {
                try {
                    // Fetch package details if package_id exists but package object not loaded
                    let packages = await this.$store.dispatch('package_list')
                    let pkg = packages.data.data.packages.find(p => p.id === packageId)
                    if (pkg) {
                        this.myPackage.id = pkg.id
                        this.myPackage.name = pkg.name || 'N/A'
                    }
                } catch (err) {
                    console.error('Error fetching package details:', err)
                }
            },
            async fetchPacakge(){
                this.myPackageLoading = true
                try{
                    let packageList = await this.$store.dispatch('package_list')
                    let pkg = packageList.data.data.packages
                    let p = []

                    for(let i = 0; i < pkg.length; i++){
                        if(this.$store.state.auth.user.level == 'user-agency' && pkg[i].is_addon == 0){
                            continue
                        }

                        p.push(pkg[i])
                    }

                    for (let i = 0; i < p.length; i++) {
                        p[i].pesan = p[i].message_quota == -1 ? "Unlimited" : p[i].message_quota
                        p[i].device = p[i].max_device == -1 ? "Unlimited" : p[i].max_device
                        p[i].customer_service = p[i].max_cs == -1 ? "Unlimited" : p[i].max_cs
                        p[i].campaign = p[i].max_campaign == -1 ? "Unlimited" : p[i].max_campaign
                        p[i].order_online = p[i].max_order_online == -1 ? "Unlimited" : p[i].max_order_online
                        p[i].link_rotator = p[i].max_link_rotator == -1 ? "Unlimited" : p[i].max_link_rotator
                        p[i].chat_widget = p[i].max_chat_widget == -1 ? "Unlimited" : p[i].max_chat_widget
                        p[i].webhook_media_quota = parseInt(p[i].webhook_media_quota)/1000000
                        if(p[i].id == this.myPackage.id){
                            p[i].isMyPackage = true
                        }else{
                            p[i].isMyPackage = false
                        }
                    }

                    this.packageList = p
                }catch(err){
                    console.log(err)
                }
                this.myPackageLoading = false
            },
            async fetchBankAccounts() {
                try {
                    let response = await this.$store.dispatch('invoice/bank_accounts_active')
                    this.bankAccountList = response.data.data.bank_accounts || []
                } catch(err) {
                    this.bankAccountList = []
                }
            },
            toggleModal(param) {
                this.$modal.toggle(param);
            },
            formatRupiah(numb){
                if (!numb && numb !== 0) return '-'
                const format = numb.toString().split('').reverse().join('');
                const convert = format.match(/\d{1,3}/g);
                const rupiah = 'Rp ' + convert.join('.').split('').reverse().join('')
                return rupiah
            },
            copyText(text) {
                navigator.clipboard.writeText(text).then(() => {
                    this.$toast.success('Disalin: ' + text)
                }).catch(() => {
                    this.$toast.error('Gagal menyalin')
                })
            },
            matchPackage(a, b){
                if(a == b){
                    return true
                }else{
                    return false
                }
            },
            isExpired(){
                // Use userExpiredAt from fetched user data
                if (this.userExpiredAt) {
                    return this.userExpiredAt.isBefore(moment())
                }

                // Fallback to store if userExpiredAt not set
                if (this.$store.state.auth.user && this.$store.state.auth.user.expired_at) {
                    let expiredAt = moment(this.$store.state.auth.user.expired_at)
                    return expiredAt.isBefore(moment())
                }

                return false
            },
            isExpiringSoon(){
                if (!this.myPackage || !this.myPackage.expired_at) return false
                let expiredDate = moment(this.myPackage.expired_at, 'DD MMMM YYYY')
                let now = moment()
                let daysUntilExpiry = expiredDate.diff(now, 'days')
                return daysUntilExpiry <= 7 && daysUntilExpiry > 0
            },
            async fetchAgencySetting(){
                let setting = await this.$store.dispatch('agency_setting_user')
                this.extendURL = setting.data.data.setting.extend_url
            },
            openPaymentMethod(pkg) {
                this.selectedPackage = pkg
                this.paymentMethod = 'transfer'
                this.selectedBankAccount = null
                this.$modal.toggle('payment_method')
            },
            closePaymentMethod() {
                this.$modal.toggle('payment_method')
                this.selectedPackage = null
                this.selectedBankAccount = null
            },
            async confirmPurchase() {
                if (!this.selectedPackage || !this.paymentMethod) return

                this.isPurchasing = true
                try {
                    let payload = {
                        package_id: this.selectedPackage.id,
                        payment_method: this.paymentMethod
                    }
                    if (this.paymentMethod === 'transfer' && this.selectedBankAccount) {
                        payload.bank_account_id = this.selectedBankAccount.id
                    }
                    let response = await this.$store.dispatch('package_purchase', payload)

                    let data = response.data.data

                    if (this.paymentMethod === 'paypal' && data.checkout_url) {
                        window.location.href = data.checkout_url
                        return
                    }

                    if (this.paymentMethod === 'transfer' && data.bank_accounts) {
                        let selectedAccounts = data.bank_accounts
                        if (this.selectedBankAccount) {
                            selectedAccounts = data.bank_accounts.filter(a => a.id === this.selectedBankAccount.id)
                        }
                        this.bankInstructions = {
                            invoice_number: data.invoice_number,
                            amount_idr: data.amount_idr,
                            unique_code: data.unique_code || 0,
                            total_transfer: data.total_transfer || data.amount_idr,
                            bank_accounts: selectedAccounts
                        }
                        this.$modal.toggle('payment_method')
                        this.$modal.toggle('bank_instructions')
                        return
                    }

                    this.$toast.error('Metode pembayaran tidak dikenal')
                } catch (err) {
                    const errorMessage = err.response?.data?.message || err.message || 'Gagal memproses pembelian'
                    this.$toast.error(errorMessage)
                } finally {
                    this.isPurchasing = false
                }
            }
        },
    };
</script>

<style scoped>
    .nav-link{
        cursor: pointer !important;
    }

    .info-box {
        padding: 15px;
        background: #f8f9fa;
        border-radius: 8px;
        transition: all 0.3s ease;
    }

    .info-box:hover {
        background: #e9ecef;
        transform: translateY(-2px);
        box-shadow: 0 4px 8px rgba(0,0,0,0.1);
    }

    .info-label {
        font-size: 12px;
        color: #6c757d;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        margin-bottom: 5px;
        display: block;
        font-weight: 600;
    }

    .info-value {
        font-size: 16px;
        color: #212529;
        font-weight: 500;
    }

    .card {
        border-radius: 12px;
        overflow: hidden;
    }

    .card-header {
        padding: 20px 30px;
        font-weight: 600;
    }

    .alert {
        border-radius: 8px;
        padding: 15px 20px;
        margin-bottom: 25px;
    }

    .package-card {
        position: relative;
        border: 2px solid #e9ecef;
        border-radius: 12px;
        padding: 25px 20px;
        text-align: center;
        transition: all 0.3s ease;
        background: #fff;
    }

    .package-card:hover {
        transform: translateY(-4px);
        box-shadow: 0 8px 16px rgba(0,0,0,0.1);
        border-color: #c7d2fe;
    }

    .package-card-active {
        border-color: #6366f1;
    }

    .package-badge {
        position: absolute;
        top: -10px;
        left: 50%;
        transform: translateX(-50%);
        background: #6366f1;
        color: #fff;
        font-size: 11px;
        padding: 3px 12px;
        border-radius: 20px;
        white-space: nowrap;
    }

    .package-name {
        font-weight: 600;
        margin-bottom: 10px;
    }

    .package-price {
        font-size: 24px;
        font-weight: 700;
        color: #1f2937;
        margin-bottom: 4px;
    }

    .package-features {
        margin: 18px 0;
        text-align: left;
    }

    .package-features li {
        padding: 6px 0;
        font-size: 14px;
        color: #4b5563;
        border-bottom: 1px dashed #f1f5f9;
    }

    .payment-method-list {
        display: flex;
        flex-direction: column;
        gap: 12px;
    }

    .payment-method-option {
        display: flex;
        align-items: center;
        gap: 15px;
        border: 2px solid #e9ecef;
        border-radius: 10px;
        padding: 15px;
        cursor: pointer;
        transition: all 0.2s ease;
        margin: 0;
    }

    .payment-method-option:hover {
        border-color: #c7d2fe;
    }

    .payment-method-option.selected {
        border-color: #6366f1;
        background: #eef2ff;
    }

    .payment-method-option input {
        margin-right: 5px;
    }

    .payment-method-icon {
        font-size: 28px;
        color: #6b7280;
        min-width: 40px;
        text-align: center;
    }

    .payment-method-icon.paypal-icon {
        color: #003087;
    }

    .payment-method-title {
        font-weight: 600;
        color: #1f2937;
    }

    .payment-method-desc {
        font-size: 12px;
        color: #6b7280;
    }
</style>
